import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Link, Stack, Typography } from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { AuthService } from '../../services/auth.service';
import { MyBreakdownService } from '../../services/maintenance.service';
import { MyBreakdownRequestView, mntLabel } from '../../interfaces/maintenance';
import ReportBreakdownDialog from './ReportBreakdownDialog';
import { REQUEST_STATUS_COLOR, fmtDateTime } from './woCommon';

const SHOWN = 5;

/**
 * Compact "machine breakdowns I reported" block: a report button, my last few requests and a link to the
 * full list. Only the caller's own requests are ever fetched (self-service endpoint, no permission key).
 * Renders nothing when it cannot load, so a board that embeds it never shows an error for this block.
 */
const MyBreakdownsPanel: React.FC = () => {
    const institutionId = AuthService.getSelectedInstitution();
    const [rows, setRows] = useState<MyBreakdownRequestView[] | null>(null);
    const [total, setTotal] = useState(0);
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) { setFailed(true); return; }
        try {
            const res = await MyBreakdownService.listMine(institutionId, 0, SHOWN);
            setRows(res.content);
            setTotal(res.totalElements);
            setFailed(false);
        } catch {
            setFailed(true);
        }
    }, [institutionId]);

    useEffect(() => { load(); }, [load]);

    const cancel = async (id: string) => {
        try {
            await MyBreakdownService.cancelMine(id);
            load();
        } catch {
            // the full page shows API errors; here a failed cancel simply leaves the row as it was
        }
    };

    if (failed) return null;

    return (
        <Box data-testid="my-breakdowns-panel">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Button data-help="maint.myRequests.report" size="small" variant="contained" startIcon={<ReportProblemIcon />} onClick={() => setOpen(true)}>
                    Report a breakdown
                </Button>
                <Box sx={{ flex: 1 }} />
                <Link component={RouterLink} to="/my/breakdown-requests" underline="hover" variant="body2">
                    {total > SHOWN ? `See all ${total}` : 'See all'}
                </Link>
            </Stack>
            {rows && rows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>You have not reported any breakdowns.</Typography>
            )}
            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                {(rows ?? []).map(r => (
                    <Stack key={r.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`my-breakdown-${r.id}`}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.requestNo}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                            {r.equipmentCode ? `${r.equipmentCode} ${r.equipmentName ?? ''}`.trim() : r.locationName ?? '-'}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary">{fmtDateTime(r.createdAt)}</Typography>
                        <Chip size="small" label={mntLabel(r.status)} color={REQUEST_STATUS_COLOR[r.status]} />
                        {r.status === 'SUBMITTED' && (
                            <Button data-help="maint.myRequests.cancel" size="small" color="inherit" onClick={() => cancel(r.id)}>Cancel</Button>
                        )}
                    </Stack>
                ))}
            </Stack>
            <ReportBreakdownDialog open={open} onClose={() => setOpen(false)} onCreated={() => { load(); }} />
        </Box>
    );
};

export default MyBreakdownsPanel;
